// src/services/offlineDb.js — tiny promise wrapper over IndexedDB.
//
// Hand-rolled rather than pulling in `idb` so the offline layer adds zero
// dependencies to the build. Only the handful of operations the outbox and read
// cache actually need are implemented.
//
// Stores:
//   outbox   — queued writes waiting for signal (auto-increment id, FIFO order)
//   reads    — cached GET responses so the app still renders with no signal
//   idmap    — placeholder -> real server id, for ops queued before their
//              parent op had been assigned an id (offline clock-in -> clock-out)

const DB_NAME = 'surveypro-offline';
const DB_VERSION = 1;

export const STORE_OUTBOX = 'outbox';
export const STORE_READS = 'reads';
export const STORE_IDMAP = 'idmap';

let dbPromise = null;

// Safari in private mode and some locked-down WebViews throw on open. Callers
// treat a null db as "offline features unavailable" rather than crashing.
export function openDb() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      resolve(null);
      return;
    }

    req.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
        const outbox = db.createObjectStore(STORE_OUTBOX, { keyPath: 'id', autoIncrement: true });
        outbox.createIndex('createdAt', 'createdAt');
        outbox.createIndex('status', 'status');
      }
      if (!db.objectStoreNames.contains(STORE_READS)) {
        db.createObjectStore(STORE_READS, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORE_IDMAP)) {
        db.createObjectStore(STORE_IDMAP, { keyPath: 'placeholder' });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });

  return dbPromise;
}

function tx(db, store, mode) {
  return db.transaction(store, mode).objectStore(store);
}

function wrap(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function dbPut(store, value) {
  const db = await openDb();
  if (!db) return null;
  return wrap(tx(db, store, 'readwrite').put(value));
}

export async function dbAdd(store, value) {
  const db = await openDb();
  if (!db) return null;
  return wrap(tx(db, store, 'readwrite').add(value));
}

export async function dbGet(store, key) {
  const db = await openDb();
  if (!db) return null;
  return wrap(tx(db, store, 'readonly').get(key));
}

export async function dbGetAll(store) {
  const db = await openDb();
  if (!db) return [];
  return wrap(tx(db, store, 'readonly').getAll());
}

export async function dbDelete(store, key) {
  const db = await openDb();
  if (!db) return null;
  return wrap(tx(db, store, 'readwrite').delete(key));
}

export async function dbClear(store) {
  const db = await openDb();
  if (!db) return null;
  return wrap(tx(db, store, 'readwrite').clear());
}

export async function dbCount(store) {
  const db = await openDb();
  if (!db) return 0;
  return wrap(tx(db, store, 'readonly').count());
}

// Rough disk headroom, used to stop photo queueing before the browser starts
// evicting our data. Not supported everywhere — callers treat null as "unknown".
export async function storageEstimate() {
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const { usage, quota } = await navigator.storage.estimate();
      if (typeof usage === 'number' && typeof quota === 'number' && quota > 0) {
        return { usage, quota, freeBytes: quota - usage, pctUsed: usage / quota };
      }
    }
  } catch (e) {
    /* not supported */
  }
  return null;
}

// Ask the browser not to evict our outbox under storage pressure. Best-effort:
// Chrome grants it silently for installed/engaged sites, Safari usually declines.
export async function requestPersistence() {
  try {
    if (navigator.storage && navigator.storage.persist) {
      if (await navigator.storage.persisted()) return true;
      return await navigator.storage.persist();
    }
  } catch (e) {
    /* not supported */
  }
  return false;
}
